import { useChapter } from '@/context/ChapterContext'
import { useVideo } from '@/context/VideoContext'
import { useVideoTexture } from '@react-three/drei'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as THREE from 'three'
import VideoElement from './VideoElement'

export default function VideoSphere() {
  const { currentVideo, pause: pauseVideo } = useVideo()
  const { isChangingChapter, setIsChangingChapter, nextChapter, getCurrentChapter, setShowHotspots, showHotspots } =
    useChapter()

  const completionHandledRef = useRef(false)
  const chapterChangeTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const [textureReady, setTextureReady] = useState(false)
  const textureReadyRef = useRef(false) // Track if we've already marked ready
  const videoLoadTimeoutRef = useRef<NodeJS.Timeout | null>(null) // Track video load timeout
  const [videoElement, setVideoElement] = useState<HTMLVideoElement | null>(null)
  const videoElementRef = useRef<HTMLVideoElement | null>(null) // Stable reference
  const previousVideoRef = useRef<string | null>(null) // Track previous video URL
  const loggedNotReadyRef = useRef(false) // Track if we've logged texture not ready

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (chapterChangeTimeoutRef.current) {
        clearTimeout(chapterChangeTimeoutRef.current)
      }
      if (videoLoadTimeoutRef.current) {
        clearTimeout(videoLoadTimeoutRef.current)
      }
    }
  }, [])

  // Reset completion flag when video changes or hotspots are hidden
  useEffect(() => {
    completionHandledRef.current = false
  }, [currentVideo, showHotspots])

  // Handle video completion logic
  const handleVideoCompletion = useCallback(() => {
    if (!videoElement || isChangingChapter || completionHandledRef.current) {
      return
    }

    const durationSeconds = videoElement.duration
    if (!Number.isFinite(durationSeconds) || durationSeconds <= 0) {
      return
    }

    const timeRemaining = durationSeconds - videoElement.currentTime
    // Only trigger at the very end - within 0.1 seconds or when video has ended
    const isRealEnd = videoElement.ended || timeRemaining <= 0.1

    // Reset flag if we're not near the end (more than 1 second remaining)
    if (!isRealEnd) {
      if (timeRemaining > 1.0) {
        completionHandledRef.current = false
      }
      return
    }

    completionHandledRef.current = true

    const chapter = getCurrentChapter()

    // Show hotspots if chapter has them
    if (chapter?.hasHotspots && !showHotspots) {
      pauseVideo()
      setShowHotspots(true)
      return
    }

    // Move to next chapter if no hotspots
    if (!chapter?.hasHotspots) {
      setIsChangingChapter(true)
      nextChapter()

      if (chapterChangeTimeoutRef.current) {
        clearTimeout(chapterChangeTimeoutRef.current)
      }
      chapterChangeTimeoutRef.current = setTimeout(() => {
        setIsChangingChapter(false)
        chapterChangeTimeoutRef.current = null
      }, 1000)
    }
  }, [
    videoElement,
    isChangingChapter,
    getCurrentChapter,
    showHotspots,
    pauseVideo,
    setShowHotspots,
    nextChapter,
    setIsChangingChapter,
  ])

  // Attach video completion listeners
  useEffect(() => {
    if (!videoElement) return

    videoElement.addEventListener('timeupdate', handleVideoCompletion)
    videoElement.addEventListener('ended', handleVideoCompletion)

    return () => {
      videoElement.removeEventListener('timeupdate', handleVideoCompletion)
      videoElement.removeEventListener('ended', handleVideoCompletion)
    }
  }, [handleVideoCompletion, videoElement])

  // Create and configure video texture
  const videoTexture = useVideoTexture(currentVideo, {
    loop: false,
    muted: true,
    start: false,
    playsInline: true,
    crossOrigin: 'anonymous',
  })

  // Configure texture properties (memoized)
  useMemo(() => {
    if (videoTexture) {
      videoTexture.needsUpdate = true
      videoTexture.flipY = false
      videoTexture.rotation = Math.PI
      videoTexture.center = new THREE.Vector2(0.5, 0.5)
      videoTexture.minFilter = THREE.LinearFilter
      videoTexture.magFilter = THREE.LinearFilter
      videoTexture.generateMipmaps = false
      videoTexture.colorSpace = THREE.SRGBColorSpace
    }
    return videoTexture
  }, [videoTexture])

  // Reset texture ready state when video URL actually changes (BEFORE sync effect)
  useEffect(() => {
    if (previousVideoRef.current !== currentVideo) {
      previousVideoRef.current = currentVideo
      textureReadyRef.current = false
      setTextureReady(false)
      videoElementRef.current = null
      loggedNotReadyRef.current = false // Reset log flag on video change

      // Clear any existing timeout
      if (videoLoadTimeoutRef.current) {
        clearTimeout(videoLoadTimeoutRef.current)
        videoLoadTimeoutRef.current = null
      }

      // Set a timeout to detect if video fails to load
      if (currentVideo) {
        videoLoadTimeoutRef.current = setTimeout(() => {
          if (!textureReadyRef.current) {
            console.error(['Video failed to load within 10s timeout', { src: currentVideo }], 'video')
          }
          videoLoadTimeoutRef.current = null
        }, 10000) // 10 second timeout
      }
    }
  }, [currentVideo])

  // Sync video element from texture and pass to VideoElement bridge
  useEffect(() => {
    const element = videoTexture?.source?.data as HTMLVideoElement | undefined

    // Update stable reference and state if element changed
    const elementChanged = element !== videoElementRef.current
    if (elementChanged) {
      videoElementRef.current = element || null
      setVideoElement(element || null)
    }

    // Check if video has valid dimensions before marking texture as ready
    if (element) {
      const checkReady = () => {
        const ready = element.readyState >= 2 && element.videoWidth > 0 && element.videoHeight > 0

        // Only update state if we haven't marked it ready yet and it's now ready
        if (ready && !textureReadyRef.current) {
          textureReadyRef.current = true
          setTextureReady(true)

          // Clear the timeout since video loaded successfully
          if (videoLoadTimeoutRef.current) {
            clearTimeout(videoLoadTimeoutRef.current)
            videoLoadTimeoutRef.current = null
          }
        }
      }

      // Check immediately (handles case where video is already loaded)
      checkReady()

      // Listen for loadeddata event (only if not already ready)
      if (!textureReadyRef.current) {
        const handleError = () => {
          console.error(['Video element error', { src: currentVideo }], 'video')
          if (videoLoadTimeoutRef.current) {
            clearTimeout(videoLoadTimeoutRef.current)
            videoLoadTimeoutRef.current = null
          }
        }

        element.addEventListener('loadeddata', checkReady)
        element.addEventListener('canplay', checkReady)
        element.addEventListener('error', handleError)

        return () => {
          element.removeEventListener('loadeddata', checkReady)
          element.removeEventListener('canplay', checkReady)
          element.removeEventListener('error', handleError)
        }
      }
    } else {
      // No element - reset ready state
      if (textureReadyRef.current) {
        textureReadyRef.current = false
        setTextureReady(false)
      }
      if (elementChanged) {
        setVideoElement(null)
        videoElementRef.current = null
      }
      videoTexture?.dispose?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoTexture, currentVideo])

  if (!currentVideo) {
    return <VideoElement videoElement={videoElement} />
  }

  if (!textureReady) {
    // Log once per video that texture is not ready (for debugging)
    if (!loggedNotReadyRef.current) {
      console.debug(['Texture not ready, waiting...', { currentVideo }], 'video')
      loggedNotReadyRef.current = true
    }
    return <VideoElement videoElement={videoElement} />
  }

  return (
    <>
      <VideoElement videoElement={videoElement} />
      <mesh>
        {/* Reduced segments from 48x32 to 32x24 for better performance */}
        <sphereGeometry args={[500, 32, 24]} scale={[-1, 1, 1]} />
        <meshBasicMaterial map={videoTexture} side={THREE.BackSide} toneMapped={false} />
      </mesh>
    </>
  )
}
